<template>
  <header>
    <div class="relative flex min-h-[65vh] flex-col">
      <!-- IMAGE -->
      <div class="absolute inset-0 -z-10 overflow-hidden">
        <div v-if="data.course_image" class="h-full w-full">
          <AppImage
            :src="data.course_image"
            class="h-full w-full object-cover"
            :alt="
              $i18n.locale === 'fr'
                ? data.course_image.alt_fr
                : data.course_image.alt
            "
            :style="`object-position: ${data.course_image.focus_css};`"
          />
          <HeroImageGradient />
        </div>

        <div
          v-else
          class="h-full w-full bg-gradient-to-tr from-blue-dark-800 to-blue-dark-600"
        />
      </div>

      <div
        class="z-10 mt-navigation-desktop flex w-full grow flex-col justify-end pt-20 text-white"
      >
        <div
          id="elTitle"
          class="grid-cols-4 pb-9 sm:pb-12 xl:grid xl:pr-container-desktop-wide"
        >
          <Container indent="wide" class="col-span-3">
            <div>
              <Breadcrumbs class="mb-7" />

              <!-- TITLE -->
              <h1
                v-if="data.heading"
                class="fade-in mb-7 max-w-title hyphens-auto text-3xl font-medium leading-9 sm:mb-9 sm:text-6xl"
                v-html="$replaceHtmlTags(data.heading)"
              />

              <div class="flex flex-wrap gap-3">
                <!-- SECTION -->
                <NuxtLink
                  :to="{
                    path: `/${route.path.split('/')[1]}`,
                    query: { section: data.section.slug },
                  }"
                >
                  <TypeTag layout="medium">
                    {{ data.section.title }}
                  </TypeTag>
                </NuxtLink>
                <!-- LANGUAGES -->
                <template v-for="lang in data.languages" :key="lang.slug">
                  <NuxtLink
                    :to="{
                      path: `/${route.path.split('/')[1]}`,
                      query: { language: lang.slug },
                    }"
                  >
                    <TypeTag layout="medium">
                      {{ lang.title }}
                    </TypeTag>
                  </NuxtLink>
                </template>
              </div>
            </div>
          </Container>
        </div>
      </div>
    </div>

    <!-- MOBILE -->
    <CourseSidebar
      class="xl:hidden"
      :page="data"
      @scroll-to-course-dates="$emit('scroll-to-course-dates')"
    />

    <!-- LEAD -->
    <div id="elLead">
      <Container indent="wide">
        <div class="grid-cols-4 xl:grid xl:pr-container-desktop-wide">
          <div
            v-if="data.lead"
            class="col-span-3 max-w-text pt-10 text-md sm:pt-16 sm:text-lg"
            v-html="data.lead"
          />
        </div>
      </Container>
    </div>
  </header>
</template>

<script setup>
const props = defineProps({
  data: { type: Object, required: true },
})

const route = useRoute()

const emit = defineEmits(['resize', 'scroll-to-course-dates'])

const textHeight = ref({})

const debouncedOnResize = useDebounceFn(onResize, 500)

// FUNCTIONS
function onResize() {
  if (window.innerWidth < 1280) {
    return
  }

  // the ref approach doesn't work initial after navigating to the page with nuxtLink (returns 0 as height except you wait for > 500ms)
  textHeight.value = {
    title: document.getElementById('elTitle')?.offsetHeight,
    lead: document.getElementById('elLead')?.offsetHeight,
  }
}

// WATCHERS
watch(
  textHeight,
  (newValue) => {
    emit('resize', newValue.title + newValue.lead)
  },
  { deep: true }
)

// MOUNTED
onMounted(async () => {
  window.addEventListener('resize', debouncedOnResize)

  // Wait for page animation to finish
  await new Promise((resolve) => setTimeout(resolve, 300))

  onResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', debouncedOnResize)
})
</script>
